import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import { ChartConfiguration } from 'chart.js';
import { UserService } from 'src/app/core/user.service';
import { BlendEvent, PermissionActions, User, UserDiskUsageApiOutput, UserInfoSetApiInput, UserInfoSetApiOutput } from 'src/app/shared/models';
import { ApiService } from 'src/app/core/api.service';
import { LoggerService } from 'src/app/core/logger.service';
import { HIGH_USAGE_COLORS, LOW_USAGE_COLORS, MEDIUM_USAGE_COLORS, UNLIMITED_USAGE_COLORS, UsageType } from './billing.model';
import { UtilService, ListItem, Pagination } from '@material-library';
import { ActivatedRoute, Router } from '@angular/router';
import { BlendService } from 'src/app/shared/services/blend.service';
import { PlanDetailsApiOutput, PlanTerm } from 'src/app/shared/models/api/plandetails.model';
import { MultiAdminService } from 'src/app/core/multiadmin.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { B64Pipe } from 'src/app/shared/pipes/b64.pipe';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { PageEvent } from '@angular/material/paginator';
import { Subscription } from 'rxjs';
import { SkuService } from 'src/app/shared/services/sku.service';
import { BillingService } from './billing.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { CommentService } from 'src/app/core/comment.service';
import { cAccountInfo } from 'src/app/shared/models/api/chargbeeprofile-api.model';
import { ChargebeeService } from 'src/app/shared/services/chargebee.service';

@Component({
  selector: 'app-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.scss'],
  providers: [MultiAdminService]
})
export class BillingComponent implements OnInit, OnDestroy {
  @Input() showSendInvoiceDialog: boolean;
  public user: User;
  public cprofile: cAccountInfo;
  public planName: string;
  public planSku: string;
  public planStart: number;
  public planExpiry: number;
  public paidThru: number;
  public planExpiryClose: boolean;
  public cancelInFuture: boolean;
  public currentTerm: string;
  public subscriptionEndDate: string;
  public showPurchaseUsersOption: boolean = false;
  public planDetails: PlanDetailsApiOutput;
  public usersLimit: number;
  public totalUsers: number = 0;
  public limit: number = 0;
  public usageType: UsageType;
  public isLoading: boolean = false;
  public isPlanLoading: boolean = false;
  public permissionActions = PermissionActions;

  public doughnutChartLabels: string[] = ['Vault', 'Files', 'Available'];
  public doughnutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    cutout: 41,
    aspectRatio: 1,
    hover: { mode: null },
    plugins: {
      tooltip: {
        enabled: false,
      }
    }
  };
  public doughnutChartDatasets: ChartConfiguration<'doughnut'>['data']['datasets'];
  public chartColors: Array<string> = LOW_USAGE_COLORS;

  public DU_STATUS_INIT = 0;
  public DU_STATUS_SUCCESS = 1;
  public DU_STATUS_INVALID = 2;
  public du = {
    status: 0,
    spinner: false,
    limit_bytes: 0,
    file_count: 0,
    bytes: 0,
    percent: 0,
    vault_file_count: 0,
    vault_bytes: 0,
    vault_percent: 0,
    sync_bytes: 0,
    sync_percent: 0
  };

  public buttonColor: 'default' | 'critical' | 'warning' = 'default';

  public isAutoRenewDialogOpen: boolean = false;
  public isAutoRenewLoading: boolean = false;
  public autoRenew: boolean = false;
  public autoRenewDescription: string = ``;

  public profileExists = false;
  public recordScrubbed = false;

  listItemsForBilling: ListItem[] = [];

  public invoices: ListItem[] = [];
  public invoiceListItems: ListItem[] = [];
  public onSendInvoice: boolean = false;
  public deviceInputError: boolean = false;
  public emails: string[] = [];
  public isSendDisabled : boolean = true;

  public pageObj: any = {
    pageIndex: 0,
    pageSize: 10,
    previousPageIndex: 0,
    length: 0,
    pageSizeOptions: [10, 20, 30, 40, 50, 100],
    alignment: 'right',
    onPageChange: (event: PageEvent) => { },
  };
  private sub: Subscription;
  public addPaymentMethodList:ListItem[] = [];

  public isSMScreen: boolean = false;
  private chargebeeSubscription: Subscription;
  private breakpointSubscription: Subscription;
  public canManageBilling: boolean;

  constructor(
    private apiService: ApiService,
    private loggerService: LoggerService,
    public userService: UserService,
    private utilService: UtilService,
    private router: Router,
    private blendService: BlendService,
    private multiadminService: MultiAdminService,
    private b64: B64Pipe,
    private snackbarService: SnackbarService,
    private store: Store<fromRoot.State>,
    private skuService: SkuService,
    private billingService: BillingService,
    public breakpointObserver: BreakpointObserver,
    private dialogService: DialogService,
    private route: ActivatedRoute,
    private commentService:CommentService,
    private chargebeeService: ChargebeeService
  ) { }

  async ngOnInit() {
    this.pageObj = {
      length: 0,
      pageSize: 10,
      pageSizeOptions: [10, 20, 30, 40, 50, 100],
      alignment: 'right',
      onPageChange: this.handlePageChange,
    };
    this.user = this.userService.getUser();
    this.planName = this.user.plan_name;
    this.sub = this.store
      .select(fromRoot.getAuthState)
      .subscribe((data) => {
        if (!data.authenticated) {
          this.loggerService.info('User is not authenicated');
          this.user = new User();
          return;
        }
        if (data.user) {
          this.user = data.user;
        }
      });
    this.canManageBilling = this.userService.checkPermission(PermissionActions.MANAGE_BILLING);

    this.breakpointSubscription = this.breakpointObserver
      .observe(['(max-width: 905px)'])
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.isSMScreen = true;
        } else {
          this.isSMScreen = false;
        }
      });
    this.addPaymentMethodList = [{
      primaryText: "No payment methods on file.",
      trail2AdornmentConfig: {
        enabled: true,
        type: 'button',
        size: 'large',
        buttonConfig: {
          label: 'Add a payment method',
          type: 'filled',
          size: 'large',
          color: 'default',
        },
        onClick: () => {
          this.router.navigate(['/settings/billing/profile']);
        }
      }
    }];
    this.init();
  }

  ngOnDestroy() {
      this.sub?.unsubscribe();
      this.chargebeeSubscription?.unsubscribe();
      this.breakpointSubscription?.unsubscribe();
  };

  private async init() {
    this.isLoading = true;
    this.usersLimit = this.userService.MAX_USERS;
    this.planExpiry = this.user.account_duedate;
    this.chargeBeeLoadSubscription()

    this.isPlanLoading = true;
    if (this.du.status == this.DU_STATUS_INIT) {
      await this.getDiskUsage();
    }
      try {
        if (this.user.is_multi_new) {
          this.showPurchaseUsersOption = true;
          const result = await this.multiadminService.getUserList();
          this.totalUsers = result.users.length;
        }
      } catch (error) {
        this.loggerService.e('Failed to load getPlanDetails', error);
      }

    this.isPlanLoading = false;
  }
  addPaymentMethodClick = ()=>{
    this.router.navigate(['/settings/billing/profile']);
  }

  private async getDiskUsage() {
    try {
      const result = await this.apiService.execute<UserDiskUsageApiOutput>(
        'userdiskusage', {}
      );
      this.du.limit_bytes = result.limit_bytes;
      this.du.file_count = result.file_count;
      this.du.bytes = result.bytes;
      this.du.percent = result.percent;
      this.du.vault_file_count = result.vault_file_count;
      this.du.vault_bytes = result.vault_bytes;
      this.du.vault_percent = result.vault_percent;
      this.du.sync_bytes = result.bytes - result.vault_bytes;
      this.du.sync_percent = result.percent - result.vault_percent;
    } catch (ex) {
      this.loggerService.error(ex);
      return;
    }

    this.du.status = this.DU_STATUS_SUCCESS;

    if (this.user.is_unlimited_plan) {
      this.chartColors = UNLIMITED_USAGE_COLORS;
    } else {
      this.usageType = UsageType.LOW;
      this.chartColors = LOW_USAGE_COLORS;
      this.buttonColor = 'default';
      if (this.du.percent > 40 && this.du.percent <= 80) {
        this.usageType = UsageType.MEDIUM;
        this.chartColors = MEDIUM_USAGE_COLORS;
        this.buttonColor = 'warning';
      } else if (this.du.percent > 80) {
        this.usageType = UsageType.HIGH;
        this.chartColors = HIGH_USAGE_COLORS;
        this.buttonColor = 'critical';
      }
    }

    this.doughnutChartDatasets = [
      {
        data: [this.du.vault_percent, this.du.sync_percent, this.du.percent >= 100 ? 0 : (100 - this.du.percent)],
        label: 'Series A',
        borderWidth: 0,
        backgroundColor: this.chartColors,
      },
    ];
  };

  onUpgradePlan = async () => {
    this.chargebeeService.openPortal();
  };

  onRenewClick() {
      this.router.navigate(['/account/renew']);
  }
  private chargeBeeLoadSubscription() {
    this.chargebeeSubscription = this.chargebeeService.getSubscription().subscribe(data => {
      if (data.isLoaded) {
      this.cprofile = data.cprofile;
      if (this.cprofile?.subscription) {
        this.planName = this.cprofile.subscription.planName;

        if (this.cprofile?.subscription?.subscriptionEndDate) {
          this.subscriptionEndDate = this.utilService.formatDate(this.cprofile.subscription?.subscriptionEndDate, 'MMM d, y');
          this.planExpiry = this.billingService.getDateFromTerm(this.cprofile.subscription.subscriptionEndDate);
        }
        if(this.cprofile.subscription.subscriptionStartDate){
          this.planStart = this.billingService.getDateFromTerm(this.cprofile.subscription.subscriptionStartDate);
        }
        this.limit = this.cprofile.subscription.user_limit;
        this.cancelInFuture = this.cprofile.subscription.status === 'non_renewing';
        this.currentTerm = this.cprofile.subscription.planTerm;
        if(this.currentTerm){
        switch (this.currentTerm) {
          case PlanTerm.ANNUAL:
            this.planExpiryClose = (this.planExpiry <= Date.now() + 86400000 * 30);
            break;
          case PlanTerm.MONTHLY:
            this.planExpiryClose = (this.planExpiry <= Date.now() + 86400000 * 15);
            break;

          default:
            this.planExpiryClose = (this.planExpiry <= Date.now() + 86400000 * 30);
            break;
        }
      }
        console.log(`${this.planExpiry} <= ${Date.now() + 8640000 * 30} > 86400000 * 30`);
      }
      this.isLoading = false;
    }
    });
  }

  onCancelAutoRenewDialog = async () => {
    this.isAutoRenewDialogOpen = false;
  };

  toggleRenewSwitch = (event: MatSlideToggleChange) => {
    this.autoRenew = event.checked;
  };
  saveAutoRenew = async () => {
    try {
      this.isAutoRenewLoading = true;
      await this.apiService.execute<UserInfoSetApiOutput>('userinfoset', <UserInfoSetApiInput>{
        is_disableautorenew: this.autoRenew ? 0 : 1
      });
      this.blendService.track(BlendEvent.CHANGE_ACCOUNT_SETTING, {is_auto_renew_enabled: this.autoRenew});
      this.userService.refresh();
      this.chargebeeService.refreshSubscription();
      this.isAutoRenewDialogOpen = false;
      this.snackbarService.showMessage('success', 'Auto renew ' + (this.autoRenew ? 'enabled.' : 'disabled.'));
    } catch (ex) {
      this.loggerService.error('An error occurred saving auto renew settings');
      this.snackbarService.showMessage(
        'critical',
        "Couldn't complete action. Try again."
      );
    } finally {
      this.isAutoRenewLoading = false;
      this.isAutoRenewDialogOpen = false;
    }
  };


  // Helper method to format amount as USD currency string
  private formatCurrency(amount: number): string {
    const formattedAmount = amount.toFixed(2); // Round to 2 decimal places
    return `$${formattedAmount}`; // Prefix with '$' symbol
  }

  onCancelClick = () => {
    this.onSendInvoice = false;
  };

  onEmailChange = (data) => {
    this.emails = data
  };

  handlePageChange = (event: PageEvent) => {
    const fromIndex = event.pageIndex * event.pageSize;
    const toIndex = fromIndex + event.pageSize;
    this.invoiceListItems = this.invoices.slice(fromIndex, toIndex);

    this.pageObj = {
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
      length: this.invoices.length,
      pageSizeOptions: [10, 20, 30, 40, 50, 100],
    };
  };

  public get canPurchaseUsers() {
    return this.userService.checkPermission(PermissionActions.MANAGE_BILLING) && this.limit < this.userService.MAX_USERS;
  }

}
